export const UPLOAD_MANIFEST_SCHEMA_VERSION = "1";

export const UPLOAD_ENTRY_TYPE = {
  FOLDER: "FOLDER",
  FILE: "FILE",
};

export const CLIENT_UPLOAD_STATUS = {
  PENDING: "PENDING",
  QUEUED: "QUEUED",
  CREATING_FOLDER: "CREATING_FOLDER",
  CREATING_FILE: "CREATING_FILE",
  UPLOADING: "UPLOADING",
  FINALISING: "FINALISING",
  COMPLETE: "COMPLETE",
  COMPLETE_WITH_ERRORS: "COMPLETE_WITH_ERRORS",
  CANCELLING: "CANCELLING",
  ERROR: "ERROR",
  CANCELLED: "CANCELLED",
  TIMED_OUT: "TIMED_OUT",
  ABANDONED: "ABANDONED",
};

function normalizePath(path) {
  return (path || "").replace(/\\/g, "/").replace(/^\/+/, "");
}

function splitPath(path) {
  return normalizePath(path).split("/").filter(Boolean);
}

export function relativeFilePath(item) {
  const path = normalizePath(item.path);
  const name = item.file?.name || "";
  if (!path) {
    return name;
  }
  if (path.endsWith(name)) {
    return path;
  }
  return `${path}${name}`;
}

export function fileSubpath(item) {
  const relativePath = relativeFilePath(item);
  const parts = splitPath(relativePath);
  if (parts.length <= 1) {
    return [];
  }
  return parts.slice(0, -1);
}

export function buildFolderPaths(items) {
  const paths = new Set();
  items.forEach((item) => {
    const parts = fileSubpath(item);
    for (let index = 1; index <= parts.length; index++) {
      paths.add(parts.slice(0, index).join("/"));
    }
  });
  return Array.from(paths).sort((a, b) => {
    const aDepth = splitPath(a).length;
    const bDepth = splitPath(b).length;
    if (aDepth !== bDepth) {
      return aDepth - bDepth;
    }
    return a.localeCompare(b);
  });
}

export function buildUploadManifest({
  folderId,
  items,
  isSecret,
  retentionSchedule,
  tags,
  metadata,
  createdAt = new Date().toISOString(),
}) {
  const now = createdAt;
  const folderEntries = buildFolderPaths(items).map((path, index) => {
    const parts = splitPath(path);
    return {
      clientEntryId: `folder-${index + 1}`,
      entryType: UPLOAD_ENTRY_TYPE.FOLDER,
      name: parts[parts.length - 1] || "",
      relativePath: path,
      subpath: parts,
      size: 0,
      mimeType: "",
      clientStatus: CLIENT_UPLOAD_STATUS.PENDING,
      serverLogId: null,
      itemId: null,
      errorCode: null,
      errorMessage: null,
      lastUpdatedAt: now,
    };
  });

  const sortedItems = items
    .map((item) => ({ item, relativePath: relativeFilePath(item) }))
    .sort((a, b) => a.relativePath.localeCompare(b.relativePath));

  const fileEntries = sortedItems.map(({ item, relativePath }, index) => ({
    clientEntryId: `file-${index + 1}`,
    entryType: UPLOAD_ENTRY_TYPE.FILE,
    name: item.file.name,
    relativePath,
    subpath: fileSubpath(item),
    size: item.file.size,
    mimeType: item.file.type || "",
    clientStatus: CLIENT_UPLOAD_STATUS.PENDING,
    serverLogId: null,
    itemId: null,
    errorCode: null,
    errorMessage: null,
    lastUpdatedAt: now,
    isSecret: isSecret ?? false,
    retentionSchedule,
    tags: tags || [],
    metadata: metadata || [],
    zeroByte: item.file.size === 0,
    redsMetadata: item.redsMetadata || null,
    uploadId: null,
    sourceHandle: item.sourceHandle || null,
    sourceHandleId: item.sourceHandleId || null,
    sourceHandleKind: item.sourceHandleKind || null,
    sourceHandlePermissionState: item.sourceHandlePermissionState || null,
    sourceRootHandle: item.sourceRootHandle || null,
    sourceRootHandleId: item.sourceRootHandleId || null,
    sourceRootHandleKind: item.sourceRootHandleKind || null,
    sourceItem: item,
  }));

  const entries = [...folderEntries, ...fileEntries];
  const totalBytes = fileEntries.reduce((total, entry) => total + Number(entry.size || 0), 0);

  return {
    schemaVersion: UPLOAD_MANIFEST_SCHEMA_VERSION,
    createdAt,
    folderId,
    isSecret: isSecret ?? false,
    retentionSchedule,
    tags: tags || [],
    metadata: metadata || [],
    summary: {
      entryCount: entries.length,
      fileCount: fileEntries.length,
      folderCount: folderEntries.length,
      totalBytes,
    },
    entries,
  };
}

export function manifestForApi(manifest) {
  return {
    schemaVersion: manifest.schemaVersion,
    createdAt: manifest.createdAt,
    folderId: manifest.folderId,
    isSecret: manifest.isSecret,
    retentionSchedule: manifest.retentionSchedule,
    tags: manifest.tags || [],
    metadata: manifest.metadata || [],
    summary: manifest.summary,
    entries: manifest.entries.map((manifestEntry) => {
      const entry = {
        clientEntryId: manifestEntry.clientEntryId,
        entryType: manifestEntry.entryType,
        name: manifestEntry.name,
        relativePath: manifestEntry.relativePath,
        subpath: manifestEntry.subpath,
        size: manifestEntry.size,
        mimeType: manifestEntry.mimeType,
      };
      if (manifestEntry.entryType === UPLOAD_ENTRY_TYPE.FILE) {
        entry.zeroByte = manifestEntry.zeroByte;
        if (manifestEntry.redsMetadata) {
          entry.redsMetadata = manifestEntry.redsMetadata;
        }
      }
      return Object.fromEntries(Object.entries(entry).filter(([, value]) => value !== undefined && value !== null));
    }),
  };
}
